import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Clock, Save, RotateCcw } from "lucide-react";
import { SHIFT_TYPES, SHIFT_LABELS, SHIFT_COLORS, DEFAULT_SHIFT_HOURS } from "../../shifts";

export const ShiftHoursTab = () => {
  const [hours, setHours] = useState(DEFAULT_SHIFT_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/shifts/config")
      .then((r) => setHours(r.data?.hours || DEFAULT_SHIFT_HOURS))
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, []);

  const setField = (type, field, value) =>
    setHours((h) => ({ ...h, [type]: { ...h[type], [field]: value } }));

  const save = () => {
    setSaving(true);
    api.put("/shifts/config", hours)
      .then((r) => { setHours(r.data?.hours || hours); toast.success("Fasce orarie turni aggiornate"); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore durante il salvataggio"))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <div data-testid="shift-hours-tab" className="bg-white border border-gray-200 rounded-lg p-4 text-[13px] text-gray-400">Caricamento impostazioni…</div>;
  }

  return (
    <div data-testid="shift-hours-tab" className="bg-white border border-gray-200 rounded-lg p-5 max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-lg bg-[#16345c]/10 text-[#16345c] flex items-center justify-center"><Clock size={18} /></span>
        <div>
          <h3 className="text-[15px] font-black text-[#16345c] leading-tight">Fasce orarie dei turni</h3>
          <p className="text-[12px] text-gray-500">Orari di riferimento per Mattina, Pomeriggio e Notte.</p>
        </div>
      </div>

      <p className="text-[13px] text-gray-600 bg-[#f0f2f5] rounded-sm px-3 py-2 my-3 leading-relaxed">
        Queste fasce orarie vengono mostrate ai responsabili e ai dipendenti quando assegnano o consultano i turni.
        Il turno di <b>Notte</b> può attraversare la mezzanotte (es. 22:00 → 06:00).
      </p>

      <div className="space-y-3">
        {SHIFT_TYPES.map((t) => (
          <div key={t} className="flex items-center gap-3 flex-wrap" data-testid={`shift-hours-row-${t}`}>
            <span className="inline-flex items-center gap-2 min-w-[130px] text-[14px] font-bold text-[#16345c]">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: SHIFT_COLORS[t] }} /> {SHIFT_LABELS[t]}
            </span>
            <label className="text-[12px] text-gray-500">Dalle</label>
            <input type="time" value={hours[t]?.start || ""} onChange={(e) => setField(t, "start", e.target.value)}
              data-testid={`shift-start-${t}`} className="border border-gray-300 rounded-sm px-2 py-1.5 text-[14px] text-[#16345c] outline-none focus:border-[#16345c]" />
            <label className="text-[12px] text-gray-500">alle</label>
            <input type="time" value={hours[t]?.end || ""} onChange={(e) => setField(t, "end", e.target.value)}
              data-testid={`shift-end-${t}`} className="border border-gray-300 rounded-sm px-2 py-1.5 text-[14px] text-[#16345c] outline-none focus:border-[#16345c]" />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-5 flex-wrap border-t border-gray-100 pt-4">
        <button onClick={save} disabled={saving} data-testid="shift-hours-save" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <Save size={15} /> {saving ? "Salvataggio…" : "Salva"}
        </button>
        <button onClick={() => setHours(DEFAULT_SHIFT_HOURS)} disabled={saving} data-testid="shift-hours-reset" className="flex items-center gap-2 border border-gray-300 text-gray-600 hover:bg-gray-100 text-[13px] font-bold px-4 py-2 rounded-sm transition-colors disabled:opacity-60">
          <RotateCcw size={15} /> Valori predefiniti
        </button>
      </div>
    </div>
  );
};
